import { taxOn } from "./shop-pricing.ts";
import type { TransferKind } from "./transfers.ts";
import type { BlankShipping, Cents, Fulfillment, GarmentType, PriceOverride, PriceRule, PriceSettings, PrintTierSetting, ProductionFee, ProductionFeeDefaults, PublicPrices, TransferPrices } from "./types.ts";

export class PricingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PricingError";
  }
}

// 2XL and up carry the upcharge, whatever the spelling: 2XL/2xl, XXL/XXXL, 6XL, 2X.
export function isUpchargeSize(size: string): boolean {
  const s = size.replace(/\s+/g, "").toUpperCase();
  const numbered = /^(\d+)XL?$/.exec(s);
  if (numbered) return Number(numbered[1]) >= 2;
  const lettered = /^(X+)L$/.exec(s);
  return lettered ? lettered[1]!.length >= 2 : false;
}

export type PrintTier = "small" | "medium" | "large";

// The hand-registered type table's print tier (prices.print), by the longest side. Garments with an S&S cost use
// printTierFor and prices.printTiers instead.
export function printTier(widthIn: number, heightIn: number): PrintTier {
  const longest = Math.max(widthIn, heightIn);
  if (longest <= 4) return "small";
  if (longest <= 10) return "medium";
  return "large";
}

export interface PriceItemInput {
  garmentType: GarmentType;
  widthIn: number;
  heightIn: number;
  quantities: Record<string, number>;
  // Set for a garment with an S&S cost (unitPricesBySize, the garment part only): each size pays its own garment price
  // plus prices.printTiers for every placement, and the type table and the 2XL upcharge do not apply. An empty map
  // counts as no cost.
  unitCentsBySize?: Record<string, Cents>;
  // The item's other placements (a multi-location item, spec section 6). Every placement pays its own print tier: from
  // prices.print on a type-table garment, from prices.printTiers on a costed one.
  extraPrints?: { widthIn: number; heightIn: number }[];
}

export interface PricedItem {
  unitCents: Cents;
  lineCents: Cents;
  count: number;
}

export interface OrderPrice {
  items: PricedItem[];
  garmentCount: number;
  subtotalCents: Cents;
  discountPct: number;
  discountCents: Cents;
  // Outbound shipping only (0 for pickup).
  shippingCents: Cents;
  // Blank shipping (BLANK_SHIPPING_DEFAULT): charged once when any item has an S&S cost and the goods total is under
  // the waiver, pickup or shipped. Stored inside orders.shipping_cents with shippingCents.
  blankShippingCents: Cents;
  // Sales tax (decision 23) on the goods after discount, with shipping when the shop taxes it; inside totalCents.
  taxCents: Cents;
  totalCents: Cents;
}

export function garmentCount(quantities: Record<string, number>): number {
  let count = 0;
  for (const [size, qty] of Object.entries(quantities)) {
    if (!Number.isInteger(qty) || qty < 0) throw new PricingError(`Invalid quantity ${qty} for size ${size}`);
    count += qty;
  }
  return count;
}

export function effectivePrices(base: PriceSettings, override: PriceOverride = {}): PriceSettings {
  const garments = { ...base.garments };
  for (const [type, patch] of Object.entries(override.garments ?? {}) as [GarmentType, PriceOverride["garments"]][]) {
    garments[type] = { ...base.garments[type], ...(patch as object) };
  }
  return {
    ...base,
    garments,
    print: { ...base.print, ...(override.print ?? {}) },
    tiers: override.tiers ?? base.tiers,
  };
}

const whole = (n: unknown) => Number.isInteger(n) && (n as number) >= 0;
const positive = (n: unknown) => Number.isInteger(n) && (n as number) > 0;

// loadSettings refuses a broken rule or broken transfer prices at startup; the pricing functions check again. A print
// charge or transfer price of 0 would give work away, so those must be above zero; shipping and the tier percentages
// may be 0.
export function ruleOk(rule: PriceRule | undefined): rule is PriceRule {
  return typeof rule === "object" && rule !== null
    && Number.isFinite(rule.blankMultiplier) && rule.blankMultiplier > 0
    && Number.isInteger(rule.roundUpToCents) && rule.roundUpToCents >= 1
    && positive(rule.printPerGarmentCents) && whole(rule.bankBps) && whole(rule.cardBps);
}

const pct = (n: unknown) => whole(n) && (n as number) <= 100;

export function productionFeeDefaultsOk(d: ProductionFeeDefaults | undefined): d is ProductionFeeDefaults {
  return typeof d === "object" && d !== null && whole(d.perGarmentCents) && whole(d.perExtraPlacementCents)
    && pct(d.transferPct) && pct(d.printSharePct) && pct(d.ownGarmentPrintSharePct)
    && (d.costedGarmentRule === undefined || d.costedGarmentRule === "share" || d.costedGarmentRule === "flat");
}

// The print tiers must be a list sorted ascending by box (each tier's box at least as wide and as tall as the one
// before, and larger in one of them), with distinct names and whole cents above zero (a free print gives work away).
export function printTiersOk(tiers: unknown): tiers is PrintTierSetting[] {
  if (!Array.isArray(tiers) || tiers.length === 0) return false;
  const size = (n: unknown) => typeof n === "number" && Number.isFinite(n) && n > 0;
  let prev: PrintTierSetting | null = null;
  for (const t of tiers as unknown[]) {
    if (typeof t !== "object" || t === null) return false;
    const tier = t as PrintTierSetting;
    if (typeof tier.name !== "string" || tier.name.trim() === "" || !size(tier.maxWidthIn) || !size(tier.maxHeightIn) || !positive(tier.cents)) return false;
    if (prev && !(tier.maxWidthIn >= prev.maxWidthIn && tier.maxHeightIn >= prev.maxHeightIn
      && (tier.maxWidthIn > prev.maxWidthIn || tier.maxHeightIn > prev.maxHeightIn))) return false;
    if (tier.maxAreaSqIn !== undefined && !size(tier.maxAreaSqIn)) return false;
    if (prev && tier.maxAreaSqIn !== undefined && !(prev.maxAreaSqIn !== undefined && tier.maxAreaSqIn > prev.maxAreaSqIn)) return false;
    prev = tier;
  }
  const withArea = (tiers as PrintTierSetting[]).filter((t) => t.maxAreaSqIn !== undefined).length;
  if (withArea !== 0 && withArea !== tiers.length) return false;
  return new Set((tiers as PrintTierSetting[]).map((t) => t.name)).size === tiers.length;
}

// Print sizes come from fitPrintSize on a 0.01 in grid; this only absorbs floating-point noise, so 4.01 in is never 4.
const SIZE_EPSILON = 1e-9;

// The first box in a sorted tier list that holds both sides, or undefined past the last. The page uses it on the
// catalog's tiers (names, boxes and two prices, never the base); the server through printTierFor.
// With areas on every tier, the first tier whose area holds the print's square inches instead.
export function tierForBox<T extends { maxWidthIn: number; maxHeightIn: number; maxAreaSqIn?: number }>(widthIn: number, heightIn: number, tiers: readonly T[]): T | undefined {
  if (tiers.length > 0 && tiers.every((t) => t.maxAreaSqIn !== undefined)) {
    const area = widthIn * heightIn;
    return tiers.find((t) => area <= t.maxAreaSqIn! + SIZE_EPSILON);
  }
  return tiers.find((t) => widthIn <= t.maxWidthIn + SIZE_EPSILON && heightIn <= t.maxHeightIn + SIZE_EPSILON);
}

// The tier one print on a costed garment pays: the first whose box holds both its width and its height. A print
// larger than the last box is refused (the spot and printer limits already stop it; this is the backstop).
export function printTierFor(widthIn: number, heightIn: number, tiers: PrintTierSetting[]): PrintTierSetting {
  if (!printTiersOk(tiers)) throw new PricingError("Print prices are not set up yet.");
  if (!(Number.isFinite(widthIn) && widthIn > 0 && Number.isFinite(heightIn) && heightIn > 0)) {
    throw new PricingError(`Invalid print size ${widthIn} x ${heightIn} in`);
  }
  const tier = tierForBox(widthIn, heightIn, tiers);
  if (!tier) {
    const last = tiers[tiers.length - 1]!;
    throw new PricingError(last.maxAreaSqIn !== undefined
      ? `This print is too large. The largest print covers ${last.maxAreaSqIn} square inches.`
      : `This print is too large. The largest print is ${last.maxWidthIn} in wide by ${last.maxHeightIn} in tall.`);
  }
  return tier;
}

// What every print on one costed garment adds to its unit price: the sum of the placements' tiers.
export function printsCents(prints: readonly { widthIn: number; heightIn: number }[], tiers: PrintTierSetting[]): Cents {
  return prints.reduce((n, p) => n + printTierFor(p.widthIn, p.heightIn, tiers).cents, 0);
}

// A costed garment's full unit price per size: its garment price plus its prints.
export function withPrints(garmentBySize: Record<string, Cents>, printCents: Cents): Record<string, Cents> {
  return Object.fromEntries(Object.entries(garmentBySize).map(([size, cents]) => [size, cents + printCents]));
}

export function transfersOk(t: TransferPrices | undefined): t is TransferPrices {
  return typeof t === "object" && t !== null
    && positive(t.perSqInCents) && positive(t.minPieceCents) && positive(t.gangSheetCents) && whole(t.shippingCents);
}

// Blank shipping (Frank 2026-10-09): S&S charges inbound shipping on a blank order under $200, so every order with a
// garment priced from an S&S cost pays prices.blankShipping.cents once, waived when its goods total (subtotal less the
// bulk discount, before outbound shipping and before the two prices) is waiveAtCents or more. Pickup pays it too: it
// is the cost of getting the blanks in, not of delivery. Transfers, the buyer's own garments and hand-registered
// garments never pay it. The settings row may leave it out; then this default applies.
export const BLANK_SHIPPING_DEFAULT: BlankShipping = { cents: 1000, waiveAtCents: 20000 };

export function blankShippingOk(b: unknown): boolean {
  if (b === undefined) return true;
  if (typeof b !== "object" || b === null || Array.isArray(b)) return false;
  const o = b as BlankShipping;
  return whole(o.cents) && whole(o.waiveAtCents);
}

export function blankShippingSetting(prices: { blankShipping?: BlankShipping }): BlankShipping {
  const b = prices.blankShipping;
  if (b === undefined) return BLANK_SHIPPING_DEFAULT;
  if (!blankShippingOk(b)) throw new PricingError("Blank shipping is not set up correctly.");
  return b;
}

// The blank-shipping base cents for an order whose goods total (subtotal less discount) is goodsCents: the setting's
// cents, or 0 at or above waiveAtCents. Only for an order with a costed garment; the caller decides that.
export function blankShippingCents(goodsCents: Cents, prices: { blankShipping?: BlankShipping }): Cents {
  const b = blankShippingSetting(prices);
  return goodsCents >= b.waiveAtCents ? 0 : b.cents;
}

// What the buyer is shown for it: the two prices (0 and 0 when waived) and the goods total that makes it free.
export interface BlankShippingQuote extends DualCents {
  freeAtCents: Cents;
}

export function blankShippingQuote(baseCents: Cents, prices: { blankShipping?: BlankShipping; rule: PriceRule }): BlankShippingQuote {
  return { ...dualPrice(baseCents, prices.rule), freeAtCents: blankShippingSetting(prices).waiveAtCents };
}

function checkRule(rule: PriceRule | undefined): asserts rule is PriceRule {
  if (!ruleOk(rule)) throw new PricingError("Prices are not set up for this garment yet.");
}

function checkTransfers(t: TransferPrices | undefined): asserts t is TransferPrices {
  if (!transfersOk(t)) throw new PricingError("Transfer prices are not set up yet.");
}

// The garment part of a costed base price: the blank's cost for one size times the multiplier, rounded to the cent
// first (so floating-point noise cannot push a whole dollar up), then up to the next multiple of roundUpToCents.
export function garmentCents(costCents: Cents, rule: PriceRule): Cents {
  checkRule(rule);
  if (!Number.isInteger(costCents) || costCents < 0) throw new PricingError(`Invalid cost ${costCents}`);
  const raw = Math.round(costCents * rule.blankMultiplier);
  return Math.ceil(raw / rule.roundUpToCents) * rule.roundUpToCents;
}

// The garment part of the base unit price per size for a garment with an S&S cost; its prints are added by tier
// (withPrints, priceOrder). A size whose cost is not a whole number of cents above zero gets no price (so it is
// refused), never its prints alone.
export function unitPricesBySize(costBySize: Record<string, Cents>, rule: PriceRule): Record<string, Cents> {
  checkRule(rule);
  const out: Record<string, Cents> = {};
  for (const [size, cost] of Object.entries(costBySize)) {
    if (Number.isInteger(cost) && cost > 0) out[size] = garmentCents(cost, rule);
  }
  return out;
}

// The garment-part unit prices for a garment row's cost_by_size, or null when it has no usable cost (null, empty, or no size with
// a cost), so the garment keeps the type table.
export function costedUnitPrices(costBySize: Record<string, Cents> | null, rule: PriceRule): Record<string, Cents> | null {
  if (costBySize === null) return null;
  if (typeof costBySize !== "object" || Array.isArray(costBySize)) throw new PricingError("This garment's price is not set up correctly.");
  const units = unitPricesBySize(costBySize, rule);
  return Object.keys(units).length > 0 ? units : null;
}

export interface DualCents {
  bankCents: Cents;
  cardCents: Cents;
}

// The two prices a buyer sees for one base price, each to the nearest cent with half a cent rounding up, in whole
// numbers: base x (10000 + bps) is exact, so there is no floating-point rounding to argue with.
export function dualPrice(baseCents: Cents, rule: PriceRule): DualCents {
  checkRule(rule);
  if (!Number.isInteger(baseCents) || baseCents < 0) throw new PricingError(`Invalid price ${baseCents}`);
  const at = (bps: number) => Math.floor((baseCents * (10000 + bps) + 5000) / 10000);
  return { bankCents: at(rule.bankBps), cardCents: at(rule.cardBps) };
}

// What a client order page may be sent: listed field by field, so nothing added to the settings later leaks.
export function publicPrices(p: PublicPrices): PublicPrices {
  return {
    placeholder: p.placeholder, garments: p.garments, print: p.print, tiers: p.tiers, shipping: p.shipping, printTiers: p.printTiers,
    ...(p.blankShipping ? { blankShipping: p.blankShipping } : {}),
    // The shop's sales tax rate (decision 23): shown as its own line, so the page needs it.
    tax: p.tax,
  };
}

// A costed line: every size ordered must have a price. unitCents is the lowest of them (order_items.unit_cents).
function sizedLine(unitBySize: Record<string, Cents>, quantities: Record<string, number>): { unitCents: Cents; lineCents: Cents } {
  let lineCents = 0;
  let unitCents = Number.POSITIVE_INFINITY;
  for (const [size, qty] of Object.entries(quantities)) {
    if (qty === 0) continue;
    const unit = Object.hasOwn(unitBySize, size) ? unitBySize[size] : undefined;
    if (unit === undefined) throw new PricingError(`There is no price for size ${size}.`);
    lineCents += qty * unit;
    unitCents = Math.min(unitCents, unit);
  }
  return { unitCents, lineCents };
}

// The quantity tier an order of this many reaches, if any.
function tierFor(total: number, tiers: PriceSettings["tiers"]): PriceSettings["tiers"][number] | undefined {
  return [...tiers].sort((a, b) => b.minQty - a.minQty).find((t) => total >= t.minQty);
}

function discountFor(total: number, subtotalCents: Cents, tiers: PriceSettings["tiers"]): { discountPct: number; discountCents: Cents } {
  const discountPct = tierFor(total, tiers)?.discountPct ?? 0;
  return { discountPct, discountCents: Math.round((subtotalCents * discountPct) / 100) };
}

export function priceOrder(items: PriceItemInput[], prices: PublicPrices, fulfillment: Fulfillment): OrderPrice {
  if (items.length === 0) throw new PricingError("An order needs at least one item");
  const priced = items.map((item) => {
    const count = garmentCount(item.quantities);
    if (count < 1) throw new PricingError("Each item needs at least one garment");
    if (!Object.hasOwn(prices.garments, item.garmentType)) throw new PricingError(`Unknown garment type ${String(item.garmentType)}`);
    const { widthIn, heightIn } = item;
    for (const p of [{ widthIn, heightIn }, ...(item.extraPrints ?? [])]) {
      if (!(Number.isFinite(p.widthIn) && p.widthIn > 0 && Number.isFinite(p.heightIn) && p.heightIn > 0)) {
        throw new PricingError(`Invalid print size ${p.widthIn} x ${p.heightIn} in`);
      }
    }
    if (item.unitCentsBySize && Object.keys(item.unitCentsBySize).length > 0) {
      const printCents = printsCents([{ widthIn, heightIn }, ...(item.extraPrints ?? [])], prices.printTiers);
      return { ...sizedLine(withPrints(item.unitCentsBySize, printCents), item.quantities), count };
    }
    const garment = prices.garments[item.garmentType];
    const tierCents = (w: number, h: number) => {
      const tier = printTier(w, h);
      return tier === "small" ? prices.print.smallCents : tier === "medium" ? prices.print.mediumCents : prices.print.largeCents;
    };
    // The garment once, then every placement's print.
    const unitCents = garment.baseCents + tierCents(widthIn, heightIn)
      + (item.extraPrints ?? []).reduce((n, p) => n + tierCents(p.widthIn, p.heightIn), 0);
    let lineCents = 0;
    for (const [size, qty] of Object.entries(item.quantities)) {
      lineCents += qty * (unitCents + (isUpchargeSize(size) ? garment.upcharge2xlCents : 0));
    }
    return { unitCents, lineCents, count };
  });
  const total = priced.reduce((n, p) => n + p.count, 0);
  const subtotalCents = priced.reduce((n, p) => n + p.lineCents, 0);
  const { discountPct, discountCents } = discountFor(total, subtotalCents, prices.tiers);
  const shippingCents = fulfillment === "ship" ? prices.shipping.baseCents + prices.shipping.perExtraItemCents * (total - 1) : 0;
  const costed = items.some((i) => i.unitCentsBySize && Object.keys(i.unitCentsBySize).length > 0);
  const blank = costed ? blankShippingCents(subtotalCents - discountCents, prices) : 0;
  const taxCents = taxOn(prices.tax, "custom", subtotalCents - discountCents, shippingCents + blank);
  return {
    items: priced,
    garmentCount: total,
    subtotalCents,
    discountPct,
    discountCents,
    shippingCents,
    blankShippingCents: blank,
    taxCents,
    totalCents: subtotalCents - discountCents + shippingCents + blank + taxCents,
  };
}

// A quantity discount as a buyer sees it (progress.md D1): the tier ("24 or more: 15% off") and what it saved, as the
// two prices. The percentage is about quantity, never the gap between bank and card.
export interface BulkDiscount {
  pct: number;
  atCount: number;
  bankCents: Cents;
  cardCents: Cents;
}

// What the tier saved, per way to pay: that method's total without the discount minus its total, both converted from
// the base by dualPrice exactly as the totals are:
//   saved = dualPrice(netCents + discountCents) - dualPrice(netCents), bank and card each.
// So it is the gap between two prices the buyer could be shown, never the base discount (which would give away the
// base). netCents is the discounted base total as quoted (shipping inside it where the total has it); count picks the
// tier. Null when no tier with a discount applies.
export function bulkDiscount(count: number, netCents: Cents, discountCents: Cents, tiers: PriceSettings["tiers"], rule: PriceRule): BulkDiscount | null {
  const tier = tierFor(count, tiers);
  if (!tier || tier.discountPct <= 0 || discountCents <= 0) return null;
  const without = dualPrice(netCents + discountCents, rule);
  const at = dualPrice(netCents, rule);
  return { pct: tier.discountPct, atCount: tier.minQty, bankCents: without.bankCents - at.bankCents, cardCents: without.cardCents - at.cardCents };
}

export interface DesignLineInput {
  unitCentsBySize: Record<string, Cents>;
  quantities: Record<string, number>;
}

export interface DesignEstimate {
  lines: { lineCents: Cents; count: number }[];
  garmentCount: number;
  subtotalCents: Cents;
  discountPct: number;
  discountCents: Cents;
  totalCents: Cents;
}

// A design request's base money: per-size unit prices, one quantity tier across every color, no shipping and no tax.
// Server only: buyers see quoteDesign's two prices.
export function priceDesign(lines: DesignLineInput[], tiers: PriceSettings["tiers"]): DesignEstimate {
  if (lines.length === 0) throw new PricingError("Pick at least one color.");
  const priced = lines.map((l) => {
    const count = garmentCount(l.quantities);
    if (count < 1) throw new PricingError("Each color needs at least one garment.");
    return { lineCents: sizedLine(l.unitCentsBySize, l.quantities).lineCents, count };
  });
  const garmentTotal = priced.reduce((n, p) => n + p.count, 0);
  const subtotalCents = priced.reduce((n, p) => n + p.lineCents, 0);
  const { discountPct, discountCents } = discountFor(garmentTotal, subtotalCents, tiers);
  return { lines: priced, garmentCount: garmentTotal, subtotalCents, discountPct, discountCents, totalCents: subtotalCents - discountCents };
}

export interface DesignQuote {
  garmentCount: number;
  // The discounted base total, blank shipping included. Stays on the server.
  baseCents: Cents;
  // The blank-shipping base cents inside baseCents (0 when waived or not charged). Stays on the server.
  blankShippingCents: Cents;
  // What the buyer sees for it, or null when the order has no costed garment (the buyer's own garments).
  blankShipping: BlankShippingQuote | null;
  bankCents: Cents;
  cardCents: Cents;
  // Per line, every size the garment is priced in: its unit price at this order's tier, as the two prices.
  units: Record<string, DualCents>[];
  // Garments to add to reach a lower price per garment, or null past the last tier.
  addForBreak: number | null;
  discount: BulkDiscount | null;
  // The tier's percentage. Stays on the server.
  discountPct: number;
}

// What a buyer is quoted: the two totals converted once from the discounted base total plus blank shipping (never
// summed from rounded units), and unit prices at the same tier for the size table. costed: the lines are garments
// priced from an S&S cost, so the order pays blank shipping; false for the buyer's own garments.
export function quoteDesign(
  lines: DesignLineInput[],
  prices: { tiers: PriceSettings["tiers"]; rule: PriceRule; blankShipping?: BlankShipping },
  costed = false,
): DesignQuote {
  const e = priceDesign(lines, prices.tiers);
  const units = lines.map((l) => unitsAtTier(l.unitCentsBySize, e.discountPct, prices.rule));
  const next = [...prices.tiers].sort((a, b) => a.minQty - b.minQty).find((t) => t.minQty > e.garmentCount && t.discountPct > e.discountPct);
  const blank = costed ? blankShippingCents(e.totalCents, prices) : 0;
  const baseCents = e.totalCents + blank;
  return {
    garmentCount: e.garmentCount, baseCents, ...dualPrice(baseCents, prices.rule), units, addForBreak: next ? next.minQty - e.garmentCount : null,
    blankShippingCents: blank, blankShipping: costed ? blankShippingQuote(blank, prices) : null,
    discount: bulkDiscount(e.garmentCount, baseCents, e.discountCents, prices.tiers, prices.rule), discountPct: e.discountPct,
  };
}

// Each size's unit price at a quantity tier, as the two prices (discountPct 0 gives the list prices).
export function unitsAtTier(unitBySize: Record<string, Cents>, discountPct: number, rule: PriceRule): Record<string, DualCents> {
  return Object.fromEntries(Object.entries(unitBySize).map(([size, unit]) => [size, dualPrice(Math.round((unit * (100 - discountPct)) / 100), rule)]));
}

// One transfer's base price: a piece pays perSqInCents for each square inch of its bounding box, rounded up to the
// cent (after dropping floating-point noise past four decimals), never under minPieceCents; a gang sheet is flat.
export function transferUnitCents(t: { kind: TransferKind; widthIn: number; heightIn: number }, p: TransferPrices): Cents {
  checkTransfers(p);
  if (t.kind === "gang") return p.gangSheetCents;
  if (!(Number.isFinite(t.widthIn) && t.widthIn > 0 && Number.isFinite(t.heightIn) && t.heightIn > 0)) {
    throw new PricingError(`Invalid transfer size ${t.widthIn} x ${t.heightIn} in`);
  }
  const cents = Math.ceil(Math.round(t.widthIn * t.heightIn * p.perSqInCents * 1e4) / 1e4);
  return Math.max(p.minPieceCents, cents);
}

export interface TransferQuote {
  quantity: number;
  // The discounted base total plus shipping. Stays on the server.
  baseCents: Cents;
  bankCents: Cents;
  cardCents: Cents;
  // One transfer at this quantity's tier, as the two prices.
  unit: DualCents;
  shippingIncluded: boolean;
  addForBreak: number | null;
  // Measured on the totals above, shipping included when they include it.
  discount: BulkDiscount | null;
}

// A transfers request: the same quantity tiers as apparel (a gang sheet counts as one piece), flat shipping added
// after the tier, and the total converted to the two prices once.
export function priceTransfers(
  t: { kind: TransferKind; widthIn: number; heightIn: number; quantity: number },
  prices: { transfers: TransferPrices; tiers: PriceSettings["tiers"]; rule: PriceRule },
  fulfillment: Fulfillment,
): TransferQuote {
  if (!Number.isInteger(t.quantity) || t.quantity < 1) throw new PricingError(`Invalid quantity ${t.quantity}`);
  const unit = transferUnitCents(t, prices.transfers);
  const subtotalCents = unit * t.quantity;
  const { discountPct, discountCents } = discountFor(t.quantity, subtotalCents, prices.tiers);
  const shippingCents = fulfillment === "ship" ? prices.transfers.shippingCents : 0;
  const baseCents = subtotalCents - discountCents + shippingCents;
  const next = [...prices.tiers].sort((a, b) => a.minQty - b.minQty).find((x) => x.minQty > t.quantity && x.discountPct > discountPct);
  return {
    quantity: t.quantity, baseCents, ...dualPrice(baseCents, prices.rule),
    unit: dualPrice(Math.round((unit * (100 - discountPct)) / 100), prices.rule),
    shippingIncluded: shippingCents > 0, addForBreak: next ? next.minQty - t.quantity : null,
    discount: bulkDiscount(t.quantity, baseCents, discountCents, prices.tiers, prices.rule),
  };
}

export function productionFee(fee: ProductionFee, count: number, netCents: Cents): Cents | null {
  if (!fee) return null;
  if (fee.mode === "per_garment") return fee.cents * count;
  return Math.round((netCents * fee.percent) / 100);
}

export interface FeeItem {
  kind: "apparel" | "transfer" | "own";
  quantities: Record<string, number>;
  prints: readonly { widthIn: number; heightIn: number }[];
  // Priced by print tier when ordered (its placements store tierCents, ruling T1-b). False or absent: the old rule.
  costed?: boolean;
}

export interface FeeParts {
  feeCents: Cents;
  // What the share was taken of: the stored print revenue (tiers after the bulk discount), or a transfer order's
  // subtotal after discount. Null when the fee is not a share (a client's own fee, the per-garment rule).
  printRevenueCents: Cents | null;
}

// An order's print revenue as stored at insert (orders.print_revenue_cents): every costed garment's print tiers times
// its garments, less the order's bulk discount percentage.
export function discountedPrintRevenue(grossCents: Cents, discountPct: number): Cents {
  return Math.round((grossCents * (100 - discountPct)) / 100);
}

// Kenny's fee for a finished order, and what it was a share of, from what the order stored when it was placed (ruling
// T1-b), never today's tiers or costs. A client with its own fee keeps it exactly as before (productionFee over every
// garment and the order's subtotal after discount). Anything else (a public design order, a client with a null fee)
// takes settings.production_fee: printSharePct percent of the stored print revenue for garments that were priced by
// tier (the blank's margin is Bag Carriers'), transferPct percent of a transfer order's subtotal after discount
// (shipping left out), and the old rule, perGarmentCents a garment plus perExtraPlacementCents for each print location
// past its first, for hand-registered garments, for orders placed before the tiers (no stored print revenue), and for
// every garment when production_fee.costedGarmentRule is "flat" (the stored revenue then stays out of the share).
// Orders on the buyer's own garments (spec 2026-10-07 section 3) pay ownGarmentPrintSharePct percent of their stored
// print revenue, which is all they were charged. A request is one product, so the transfer share is the whole order's
// and an own-garment order has no other items.
export function productionFeeParts(
  order: { subtotal_cents: Cents; discount_cents: Cents; print_revenue_cents?: Cents | null },
  items: readonly FeeItem[],
  clientFee: ProductionFee,
  defaults: ProductionFeeDefaults | null,
): FeeParts {
  const netCents = order.subtotal_cents - order.discount_cents;
  // A shop with no outside printer fee prints its own: nothing is owed, whatever a client's override says (Task A5).
  if (!defaults) return { feeCents: 0, printRevenueCents: null };
  if (clientFee) {
    return { feeCents: productionFee(clientFee, items.reduce((n, i) => n + garmentCount(i.quantities), 0), netCents)!, printRevenueCents: null };
  }
  const stored = order.print_revenue_cents ?? null;
  let fee = 0;
  let revenue: Cents | null = null;
  const apparel = items.filter((i) => i.kind === "apparel");
  const shared = stored !== null && defaults.costedGarmentRule !== "flat" && apparel.some((i) => i.costed);
  for (const item of apparel.filter((i) => !(shared && i.costed))) {
    const count = garmentCount(item.quantities);
    fee += defaults.perGarmentCents * count + defaults.perExtraPlacementCents * Math.max(0, item.prints.length - 1) * count;
  }
  if (shared) {
    revenue = stored;
    fee += Math.round((stored * defaults.printSharePct) / 100);
  }
  const own = items.filter((i) => i.kind === "own");
  if (own.length > 0 && stored !== null) {
    revenue = stored;
    fee += Math.round((stored * defaults.ownGarmentPrintSharePct) / 100);
  } else {
    for (const item of own) {
      const count = garmentCount(item.quantities);
      fee += defaults.perGarmentCents * count + defaults.perExtraPlacementCents * Math.max(0, item.prints.length - 1) * count;
    }
  }
  if (items.some((i) => i.kind === "transfer")) {
    revenue = Math.max(0, netCents);
    fee += Math.round((netCents * defaults.transferPct) / 100);
  }
  return { feeCents: Math.max(0, fee), printRevenueCents: revenue };
}

export function productionFeeFor(
  order: { subtotal_cents: Cents; discount_cents: Cents; print_revenue_cents?: Cents | null },
  items: readonly FeeItem[],
  clientFee: ProductionFee,
  defaults: ProductionFeeDefaults | null,
): Cents {
  return productionFeeParts(order, items, clientFee, defaults).feeCents;
}

// Sum over the sizes ordered of quantity times the garment's cost for that size; null when there is no cost or a size
// ordered has none. Read from the same garment row the price came from, never a second query.
export function blankCostCents(costBySize: Record<string, number> | null, quantities: Record<string, number>): number | null {
  if (!costBySize) return null;
  let total = 0;
  for (const [size, n] of Object.entries(quantities)) {
    if (!n) continue;
    const c = costBySize[size];
    if (typeof c !== "number" || !Number.isFinite(c)) return null;
    total += n * c;
  }
  return Math.round(total);
}
